import type {
  CommentDiff,
  CommentLine,
  ReconBridge,
  ReconBridges,
  ReconCell,
  ReconMeasures,
  ReconPost,
  ReconStatus,
  CommentDay,
  CommentReport,
  ReconSource,
  ReconWindow,
  SkyRead,
} from '../../hooks/socialdash/useSkyReconciliation'

/**
 * Plain-English wording and small sums for the reconciliation page. Kept apart
 * from the page so it is tested without a render.
 */

/** Short labels for each reason code (the server sends the full sentence). */
export const REASON_LABELS: Record<string, string> = {
  unexplained: 'Being checked',
  multi_tag_rows: "Counted once per programme tag in Sky's table",
  extra_campaign: 'Corporate Comms campaign, added by agreement (11 Aug 2026)',
  moved_since_read: 'Same posts, newer numbers in Sprinklr',
  comment_only_post: 'Known only from comments',
  stale_reply_post: "No longer listed in Sky's dashboard",
  platform_not_imported: "Platform Platinum doesn't cover",
  not_imported_yet: "Published after Platinum's last import",
  sky_own_post: "Sky's own post — counted as a post",
}

/** Unexplained first, then the rest in a fixed, readable order. */
export const REASON_ORDER = [
  'unexplained',
  'multi_tag_rows',
  'extra_campaign',
  'moved_since_read',
  'comment_only_post',
  'stale_reply_post',
  'platform_not_imported',
  'not_imported_yet',
]

export function reasonLabel(code: string): string {
  return REASON_LABELS[code] ?? code
}

const PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'X',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  threads: 'Threads',
}

export function platformLabel(p: string): string {
  return PLATFORM_LABELS[p.toLowerCase()] ?? p
}

/** A campaign's name; the sentinels read as words. */
export function campaignLabel(name: string, id: string): string {
  if (name) return name
  if (id === 'unknown' || id === 'none' || id === '') return 'No campaign'
  return id
}

/** "2026-03" → "Mar 2026". */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-GB')
}

/** 54,120,000 → "54.12M"; under a million in full. */
export function fmtReach(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  return fmtInt(n)
}

/** The Platinum side a viewer is shown, per the switches in force. */
export function oursFor(cell: ReconCell, posts: boolean, asTable: boolean): ReconMeasures {
  if (!posts) return cell.ours
  return asTable ? cell.oursAsTable : cell.oursInTable
}

/** The step-by-step sum for the switches in force; null on an old report. */
export function bridgeFor(bridges: ReconBridges | undefined, posts: boolean, asTable: boolean): ReconBridge | null {
  if (!bridges) return null
  if (!posts) return bridges.switchesOff
  return asTable ? bridges.countAsTable : bridges.postsInTable
}

export interface HeadlineRow {
  label: string
  sprinklr: string
  platinum: string
  same: boolean
}

/**
 * The headline: Sprinklr beside Platinum on Sky's terms (the dashboards'
 * number with the known adjustments made), for each measure. They differ
 * only by what is not explained.
 */
export function headlineRows(b: ReconBridge): HeadlineRow[] {
  const keys: Array<[string, keyof ReconMeasures]> = [
    ['Posts', 'posts'],
    ['Reach', 'reach'],
    ['Views', 'views'],
    ['Engagements', 'engagements'],
  ]
  return keys.map(([label, k]) => ({
    label,
    sprinklr: fmtInt(b.sprinklr[k]),
    platinum: fmtInt(b.matched[k]),
    same: Math.abs(b.sprinklr[k] - b.matched[k]) < 1,
  }))
}

/** "+2,000" / "−50" / "0": one step of the sum. */
export function signed(n: number): string {
  if (Math.abs(n) < 1) return '0'
  return `${n > 0 ? '+' : '−'}${fmtInt(Math.abs(n))}`
}

/** Known reasons carry no colour; only unexplained posts beyond the tolerance are an error. */
export function verdictSeverity(status: ReconStatus | undefined): 'success' | 'info' | 'error' {
  if (status === 'match') return 'success'
  if (status === 'floaters') return 'info'
  return 'error'
}

/** The colour unexplained posts are drawn in: blue within the tolerance, red beyond it. */
export function unexplainedColor(status: ReconStatus | undefined): 'info' | 'error' {
  return status === 'unexplained' ? 'error' : 'info'
}

/** "+1.2%" / "−0.4%" / "same": Platinum against Sky for one figure. */
export function diffLabel(ours: number, sky: number): string {
  if (Math.abs(ours - sky) < 1) return 'same'
  if (sky === 0) return ours > 0 ? 'only in Platinum' : 'same'
  const pct = ((ours - sky) / sky) * 100
  const sign = pct > 0 ? '+' : '−'
  if (Math.abs(pct) < 0.05) return `${sign}<0.1%`
  return `${sign}${Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`
}

/** A cell's reasons, unexplained first, as "label (n)". */
export function cellReasons(cell: ReconCell): string[] {
  const r = cell.reasons ?? {}
  return REASON_ORDER.filter((k) => r[k]).map((k) => `${reasonLabel(k)} (${r[k]})`)
}

/** Posts grouped by reason, in REASON_ORDER. */
export function postsByReason(posts: ReconPost[]): Array<{ reason: string; posts: ReconPost[] }> {
  const groups = new Map<string, ReconPost[]>()
  for (const p of posts) {
    const g = groups.get(p.reason) ?? []
    g.push(p)
    groups.set(p.reason, g)
  }
  const known = REASON_ORDER.filter((k) => groups.has(k))
  const other = [...groups.keys()].filter((k) => !REASON_ORDER.includes(k)).sort()
  return [...known, ...other].map((reason) => ({ reason, posts: groups.get(reason) ?? [] }))
}

/** A post link only when it is http(s); anything else renders as text. */
export function safeHref(url: string | undefined): string | undefined {
  if (!url) return undefined
  return /^https?:\/\//i.test(url.trim()) ? url.trim() : undefined
}

/** "" when a row's reasons add up; otherwise what is left over. */
export function residualNote(cell: ReconCell): string {
  const r = cell.residual
  if (!r) return ''
  const parts: string[] = []
  if (r.posts) parts.push(`${fmtInt(r.posts)} post${Math.abs(r.posts) !== 1 ? 's' : ''}`)
  if (r.reach) parts.push(`${fmtInt(r.reach)} reach`)
  if (r.views) parts.push(`${fmtInt(r.views)} views`)
  if (r.engagements) parts.push(`${fmtInt(r.engagements)} engagements`)
  return parts.length ? `Reasons do not add up: ${parts.join(', ')} left over` : ''
}


// ─── The on-demand page (design/2026-10-19-sky-reconciliation-on-demand.md) ───

/** The four platforms Platinum imports from Sprinklr, in the order the page lists them. */
export const RECON_PLATFORMS = ['facebook', 'instagram', 'tiktok', 'twitter'] as const

const DAY_MS = 24 * 60 * 60 * 1000
const DEFAULT_DAYS = 90
/** sprinklrrecon.MaxWindowDays. */
const MAX_DAYS = 366
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export interface ReconQuery {
  /** UTC dates, YYYY-MM-DD, `to` inclusive. */
  from: string
  to: string
  /** A subset of RECON_PLATFORMS, in that order. */
  platforms: string[]
}

/** "2026-10-17" for the UTC day holding ms. */
export function utcDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

function dayMs(date: string): number {
  if (!DATE_RE.test(date)) return NaN
  return Date.parse(`${date}T00:00:00Z`)
}

/** The server's default: the last 90 UTC days, ending yesterday. */
export function defaultReconRange(now: Date): { from: string; to: string } {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const to = today - DAY_MS
  return { from: utcDay(to - (DEFAULT_DAYS - 1) * DAY_MS), to: utcDay(to) }
}

/** "x" is Sprinklr's other name for Twitter. */
function canonicalPlatform(p: string): string {
  const v = p.trim().toLowerCase()
  return v === 'x' ? 'twitter' : v
}

/**
 * The page's state from the URL. A link reproduces the view; anything the URL
 * gets wrong (a malformed date, a platform we do not import) falls back to the
 * default rather than to an error, except a backwards range, which the server
 * refuses with its own sentence.
 */
export function readReconQuery(params: Record<string, string | undefined>, now: Date): ReconQuery {
  const def = defaultReconRange(now)
  const from = params.from ?? ''
  const to = params.to ?? ''
  const datesOK = !Number.isNaN(dayMs(from)) && !Number.isNaN(dayMs(to))
  const asked = new Set((params.platforms ?? '').split(',').map(canonicalPlatform).filter(Boolean))
  let platforms: string[] = RECON_PLATFORMS.filter((p) => asked.has(p))
  if (platforms.length === 0) platforms = [...RECON_PLATFORMS]
  return { from: datesOK ? from : def.from, to: datesOK ? to : def.to, platforms }
}

/**
 * The URL params and the request params, which are the same. Platforms are
 * left out when all four are chosen, so the server still lists Sky's comments
 * on platforms we do not import.
 */
export function reconQueryParams(q: ReconQuery): Record<string, string> {
  const out: Record<string, string> = { from: q.from, to: q.to }
  const chosen = RECON_PLATFORMS.filter((p) => q.platforms.includes(p))
  if (chosen.length < RECON_PLATFORMS.length) out.platforms = chosen.join(',')
  return out
}

/** Why a range cannot be applied, or "" when it can. */
export function reconQueryProblem(q: ReconQuery): string {
  const f = dayMs(q.from)
  const t = dayMs(q.to)
  if (Number.isNaN(f) || Number.isNaN(t)) return 'Choose a from date and a to date.'
  if (t < f) return 'The from date is after the to date.'
  if ((t - f) / DAY_MS + 1 > MAX_DAYS) return 'Choose a range of 12 months or less.'
  if (q.platforms.length === 0) return 'Choose at least one platform.'
  return ''
}

/** "17 Oct 2026", the UTC day. */
export function utcDayLabel(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/** "23:05 UTC". */
export function utcTime(ms: number): string {
  const d = new Date(ms)
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${hh}:${mm} UTC`
}

/** "17 Oct 2026, 23:05 UTC". */
export function utcDateTime(ms: number): string {
  return `${utcDayLabel(ms)}, ${utcTime(ms)}`
}

/**
 * Where and when Sprinklr was read and how far Platinum's import has got, all
 * UTC. The call count and the cache stay in the JSON, never on screen.
 */
export function provenanceLine(r: { sky: SkyRead; frontierMs?: number; source?: ReconSource }): string {
  const parts: string[] = []
  const names = (r.source?.widgets ?? []).map((w) => w.name).filter(Boolean)
  const widgets = names.length ? ` (${names.join(' and ')} widget${names.length === 1 ? '' : 's'})` : ''
  const where = r.source?.dashboard ? `Sky's dashboard "${r.source.dashboard}"` : "Sky's Sprinklr dashboard"
  parts.push(`Sprinklr read ${utcDateTime(r.sky.readAtMs)} from ${where}${widgets}`)
  if (r.frontierMs && r.frontierMs > 0) parts.push(`Platinum's import complete to ${utcDateTime(r.frontierMs)}`)
  parts.push('all days are UTC.')
  return parts.join(' · ')
}

/** X's comment kinds, in the order the page lists them; the three always appear. */
const TWITTER_KINDS = ['reply', 'mention', 'repost'] as const
const TWITTER_EXTRA_KINDS = ['other', 'unclassified']

/** The line, on every platform, that carries Sky's own posts (sprinklrrecon KindOwnPosts). */
export const OWN_POSTS_KIND = 'own_posts'

/** Sky's own posts: in Sprinklr's comment count, posts in Platinum's. Never in a comment total. */
export function isOwnPostsLine(l: Pick<CommentLine, 'kind' | 'reason'>): boolean {
  return l.reason === 'sky_own_post' || l.kind === OWN_POSTS_KIND || l.kind === 'update'
}

const KIND_LABELS: Record<string, string> = {
  own_posts: "Sky's own posts (counted as posts, not comments)",
  update: "Sky's own posts (counted as posts, not comments)",
  reply: 'Replies',
  mention: 'Mentions',
  repost: 'Reposts',
  other: 'Other kinds',
  unclassified: 'Not split by kind',
}

export function commentKindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind
}

/** "X replies", "Instagram": a differing day's platform and kind in words. */
export function commentDayLabel(platform: string, kind: string): string {
  const p = platformLabel(platform)
  if (!kind) return p
  return `${p} ${commentKindLabel(kind).toLowerCase()}`
}

export interface CommentRow {
  platform: string
  kind: string
  /** The platform, or for X the kind. */
  label: string
  sky: number
  ours: number
  imported: boolean
  /** "sky_own_post": a neutral line outside the totals. */
  reason?: string
}

/**
 * The comment table's rows: imported platforms in RECON_PLATFORMS order (X as
 * its three kinds, then any it could not split), each followed by its own-posts
 * line when Sprinklr counted any, then Sky's platforms we do not import.
 */
export function commentRows(lines: CommentLine[] | null | undefined): CommentRow[] {
  const all = lines ?? []
  const by = new Map(all.map((l) => [`${canonicalPlatform(l.platform)}|${l.kind}`, l]))
  const has = new Set(all.map((l) => canonicalPlatform(l.platform)))
  const rows: CommentRow[] = []
  for (const p of RECON_PLATFORMS) {
    if (p === 'twitter') {
      if (!has.has('twitter')) continue
      for (const k of [...TWITTER_KINDS, ...TWITTER_EXTRA_KINDS]) {
        const l = by.get(`twitter|${k}`)
        if (!l && !(TWITTER_KINDS as readonly string[]).includes(k)) continue
        rows.push({ platform: 'twitter', kind: k, label: commentKindLabel(k), sky: l?.sky ?? 0, ours: l?.ours ?? 0, imported: l?.imported ?? true, reason: l?.reason || undefined })
      }
    } else {
      const l = by.get(`${p}|`)
      if (l) rows.push({ platform: p, kind: '', label: platformLabel(p), sky: l.sky, ours: l.ours, imported: l.imported })
    }
    for (const l of all.filter((x) => canonicalPlatform(x.platform) === p && isOwnPostsLine(x))) {
      rows.push({ platform: p, kind: l.kind, label: commentKindLabel(OWN_POSTS_KIND), sky: l.sky, ours: l.ours, imported: l.imported, reason: 'sky_own_post' })
    }
  }
  const known = new Set<string>(RECON_PLATFORMS)
  const rest = all
    .filter((l) => !known.has(canonicalPlatform(l.platform)))
    .sort((a, b) => a.platform.localeCompare(b.platform) || a.kind.localeCompare(b.kind))
  for (const l of rest) {
    rows.push({ platform: l.platform, kind: l.kind, label: l.kind ? commentDayLabel(l.platform, l.kind) : platformLabel(l.platform), sky: l.sky, ours: l.ours, imported: l.imported, reason: l.reason })
  }
  return rows
}

/** Short labels for each comment difference reason (sprinklrrecon/comments.go). */
export const COMMENT_REASON_LABELS: Record<string, string> = {
  missed: 'Not in Platinum — being checked',
  not_imported_yet: "Arrived after Platinum's last import",
  held_from_apify: 'Held from an earlier import',
  gone_from_sprinklr: 'No longer in Sprinklr',
  platform_not_imported: "Platform Platinum doesn't cover",
  sky_own_post: "Sky's own post — counted as a post",
}

/** What each reason means, in a sentence. */
export const COMMENT_REASON_EXPLANATIONS: Record<string, string> = {
  missed: 'Sprinklr lists these for a day Platinum has already imported. Either Sprinklr added them to that day later, or the import skipped them.',
  not_imported_yet: 'The next import brings them in.',
  held_from_apify: 'Platinum already holds this message from an earlier source and keeps one copy off the dashboards.',
  gone_from_sprinklr: 'Deleted, hidden, or moved out of a campaign since Platinum imported it.',
  platform_not_imported: "Sprinklr holds comments on a platform Platinum doesn't cover.",
  sky_own_post: "Sky's own posts are posts, not comments, so Platinum counts them with posts.",
}

/** What to do about a message still being checked. */
export const BEING_CHECKED_NEXT_STEP = 'To follow one up, quote its Sprinklr id to Platinum support.'

/** Being checked first: the only reason anyone needs to act on. */
export const COMMENT_REASON_ORDER = ['missed', 'not_imported_yet', 'held_from_apify', 'gone_from_sprinklr', 'platform_not_imported', 'sky_own_post']

export function commentReasonLabel(code: string): string {
  return COMMENT_REASON_LABELS[code] ?? code
}

/**
 * `missed` is amber, never red: Sprinklr adds late comments to past days and
 * keeps each one's original time, so "added later" and "skipped" cannot be told
 * apart by time (Kai, 19 Oct 2026).
 */
export function commentReasonColor(code: string): 'warning' | 'default' {
  return code === 'missed' ? 'warning' : 'default'
}

/** A day's differing messages grouped by reason, in COMMENT_REASON_ORDER. */
export function commentDiffsByReason(diffs: CommentDiff[] | null | undefined): Array<{ reason: string; diffs: CommentDiff[] }> {
  const groups = new Map<string, CommentDiff[]>()
  for (const d of diffs ?? []) {
    const g = groups.get(d.reason) ?? []
    g.push(d)
    groups.set(d.reason, g)
  }
  const known = COMMENT_REASON_ORDER.filter((k) => groups.has(k))
  const other = [...groups.keys()].filter((k) => !COMMENT_REASON_ORDER.includes(k)).sort()
  return [...known, ...other].map((reason) => ({ reason, diffs: groups.get(reason) ?? [] }))
}


// ─── The page laid out for confidence (design/2026-10-19-sky-reconciliation-page-redesign.md) ───

/** "18 Jul – 17 Oct 2026"; the first year is named only when it differs. */
export function rangeLabel(startMs: number, endMs: number): string {
  const a = new Date(startMs)
  const b = new Date(endMs)
  const end = utcDayLabel(endMs)
  if (a.getUTCFullYear() === b.getUTCFullYear()) {
    return `${a.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })} – ${end}`
  }
  return `${utcDayLabel(startMs)} – ${end}`
}

export type VerdictTone = 'success' | 'info' | 'error'

/**
 * The page's one sentence. Posts drive it: a post either has a reason or needs
 * a look. Comment days are explained on a click ("See why"), so they do not
 * hedge it.
 */
export function verdictLine(
  posts: { status?: ReconStatus; unexplainedPosts?: number },
  window: Pick<ReconWindow, 'startMs' | 'endMs'>,
): { tone: VerdictTone; text: string } {
  const range = rangeLabel(window.startMs, window.endMs)
  const n = posts.unexplainedPosts ?? 0
  const nPosts = `${fmtInt(n)} post${n === 1 ? '' : 's'}`
  const beingChecked = `${nPosts} ${n === 1 ? 'is' : 'are'} being checked`
  if (posts.status === 'match') {
    return { tone: 'success', text: `Platinum reflects Sprinklr for ${range}. Every difference below has a reason.` }
  }
  if (posts.status === 'floaters') {
    return { tone: 'info', text: `Platinum reflects Sprinklr for ${range}. ${beingChecked}; every other difference has a reason.` }
  }
  if (n === 0) {
    return { tone: 'error', text: `For ${range}, every post has a reason, but some sums do not add up yet. They are shown in the working.` }
  }
  return { tone: 'error', text: `${nPosts} ${n === 1 ? 'differs' : 'differ'} from Sprinklr without a reason yet. ${n === 1 ? 'It is' : 'They are'} listed first.` }
}

export interface Figure {
  sprinklr: string
  platinum: string
  /** Platinum less Sprinklr. */
  diff: string
  pct: string
  same: boolean
}

/** Sprinklr beside Platinum for one measure, with the difference and percent. */
export function figure(sprinklr: number, platinum: number, fmt: (n: number) => string = fmtInt): Figure {
  return {
    sprinklr: fmt(sprinklr),
    platinum: fmt(platinum),
    diff: signed(platinum - sprinklr),
    pct: diffLabel(platinum, sprinklr),
    same: Math.abs(platinum - sprinklr) < 1,
  }
}

export interface LedgerRow {
  platform: string
  kind: string
  label: string
  sky: number
  ours: number
  /** An X kind under the X heading row. */
  child?: boolean
}

export interface CommentLedger {
  /** Imported platforms; X as a heading row (its comment kinds summed) and its kinds under it. */
  rows: LedgerRow[]
  /** Sky's own posts, per platform Platinum covers: in Sprinklr's comment count, posts in Platinum's. */
  skyOwnPosts: Array<{ platform: string; label: string; sky: number }>
  skyOwnPostsTotal: number
  /** Sprinklr's comments on platforms Platinum doesn't cover. */
  notCovered: Array<{ platform: string; label: string; sky: number }>
  notCoveredTotal: number
}

/**
 * The comments ledger: what is compared, and, apart, what is in Sprinklr but
 * outside the comparison (Sky's own posts on each platform, platforms Platinum
 * doesn't cover).
 */
export function commentLedger(lines: CommentLine[] | null | undefined): CommentLedger {
  const all = lines ?? []
  const rows: LedgerRow[] = []
  const skyOwnPosts: CommentLedger['skyOwnPosts'] = []
  for (const p of RECON_PLATFORMS) {
    const covered = all.filter((l) => canonicalPlatform(l.platform) === p && l.imported)
    const own = covered.filter(isOwnPostsLine).reduce((a, l) => a + l.sky, 0)
    if (own > 0) skyOwnPosts.push({ platform: p, label: platformLabel(p), sky: own })
    const mine = covered.filter((l) => !isOwnPostsLine(l))
    if (mine.length === 0) continue
    if (p !== 'twitter') {
      const l = mine.find((x) => !x.kind) ?? mine[0]
      rows.push({ platform: p, kind: '', label: platformLabel(p), sky: l.sky, ours: l.ours })
      continue
    }
    const kinds: LedgerRow[] = []
    for (const k of [...TWITTER_KINDS, ...TWITTER_EXTRA_KINDS]) {
      const l = mine.find((x) => x.kind === k)
      if (!l && !(TWITTER_KINDS as readonly string[]).includes(k)) continue
      kinds.push({ platform: 'twitter', kind: k, label: commentKindLabel(k), sky: l?.sky ?? 0, ours: l?.ours ?? 0, child: true })
    }
    rows.push({
      platform: 'twitter', kind: '', label: platformLabel('twitter'),
      sky: kinds.reduce((a, r) => a + r.sky, 0), ours: kinds.reduce((a, r) => a + r.ours, 0),
    })
    rows.push(...kinds)
  }
  const known = new Set<string>(RECON_PLATFORMS)
  const byPlatform = new Map<string, number>()
  for (const l of all) {
    const p = canonicalPlatform(l.platform)
    if ((known.has(p) && l.imported) || isOwnPostsLine(l)) continue
    byPlatform.set(p, (byPlatform.get(p) ?? 0) + l.sky)
  }
  const notCovered = [...byPlatform.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([platform, sky]) => ({ platform, label: platformLabel(platform), sky }))
  return {
    rows, skyOwnPosts, skyOwnPostsTotal: skyOwnPosts.reduce((a, r) => a + r.sky, 0),
    notCovered, notCoveredTotal: notCovered.reduce((a, r) => a + r.sky, 0),
  }
}

/** Whole UTC days in the window. */
function windowDays(window: Pick<ReconWindow, 'startMs' | 'endMs'>): number {
  return Math.max(0, Math.round((window.endMs + 1 - window.startMs) / DAY_MS))
}

/**
 * "25 of 368 days differ, counting each platform's days separately; 343 match
 * exactly." X's kinds on one day are one day. With one platform, just days.
 */
export function daysSummary(
  days: CommentDay[] | null | undefined,
  platforms: number,
  window: Pick<ReconWindow, 'startMs' | 'endMs'>,
  frontierMs?: number,
): { differ: number; total: number; text: string } {
  const all = days ?? []
  const key = (d: CommentDay) => `${canonicalPlatform(d.platform)}|${d.dayMs}`
  const differ = new Set(all.filter((d) => !dayAfterImport(d.dayMs, frontierMs)).map(key)).size
  const later = new Set(all.filter((d) => dayAfterImport(d.dayMs, frontierMs)).map(key)).size
  const { days: n, cut } = importedDays(window, frontierMs)
  const total = platforms * n
  const verb = differ === 1 ? 'differs' : 'differ'
  const each = platforms > 1 ? ", counting each platform's days separately" : ''
  let text = `${fmtInt(differ)} of ${fmtInt(total)} ${cut ? 'imported ' : ''}days ${verb}${each}; ${fmtInt(Math.max(0, total - differ))} match exactly.`
  if (later > 0) text += ` ${fmtInt(later)} later day${later === 1 ? ' is' : 's are'} not imported yet.`
  return { differ, total, text }
}

/** The window's days Platinum has imported in full, and whether the import's frontier cut the window short. */
export function importedDays(window: Pick<ReconWindow, 'startMs' | 'endMs'>, frontierMs?: number): { days: number; cut: boolean } {
  const all = windowDays(window)
  if (!frontierMs || frontierMs <= 0) return { days: all, cut: false }
  const full = Math.floor((Math.floor(frontierMs / DAY_MS) * DAY_MS - window.startMs) / DAY_MS)
  const days = Math.max(0, Math.min(all, full))
  return { days, cut: days < all }
}

/** A day Platinum has not finished importing: it ends after the import's frontier. */
export function dayAfterImport(dayMs: number, frontierMs: number | undefined): boolean {
  return Boolean(frontierMs && frontierMs > 0 && dayMs + DAY_MS > frontierMs)
}

/** The line under a platform's figure: how many of its days match, and what came after the last import. */
export function platformDaysLine(days: CommentDay[] | null | undefined, platform: string, window: Pick<ReconWindow, 'startMs' | 'endMs'>, frontierMs?: number): string {
  const mine = (days ?? []).filter((d) => canonicalPlatform(d.platform) === platform && !dayAfterImport(d.dayMs, frontierMs))
  const differ = new Set(mine.map((d) => d.dayMs)).size
  const { days: total, cut } = importedDays(window, frontierMs)
  const what = cut ? 'imported day' : 'day'
  if (differ === 0) return `Every ${what} matches exactly`
  return `${fmtInt(total - differ)} of ${fmtInt(total)} ${what}s match exactly`
}

/** A ledger row over the days Platinum has imported, and what Sprinklr has on the later days. */
export interface LedgerRowToImport extends LedgerRow {
  later: number
}

/**
 * The comments table over the days Platinum has imported: each row and the
 * total less the later days on both sides, with Sprinklr's own count for those
 * days apart (`later`), so a day not yet imported never reads as a shortfall
 * and nothing rests on a subtraction.
 */
export function ledgerToImport(
  rows: LedgerRow[],
  report: Pick<CommentReport, 'skyTotal' | 'oursTotal' | 'days'>,
  frontierMs: number | undefined,
): { rows: LedgerRowToImport[]; total: { sky: number; ours: number; later: number }; hasLater: boolean; lastFullDayMs: number } {
  const late = (report.days ?? []).filter((d) => dayAfterImport(d.dayMs, frontierMs))
  const out = rows.map((r) => {
    const mine = late.filter((d) => canonicalPlatform(d.platform) === r.platform && (!r.child || d.kind === r.kind))
    const sky = mine.reduce((a, d) => a + d.sky, 0)
    const ours = mine.reduce((a, d) => a + d.ours, 0)
    return { ...r, sky: r.sky - sky, ours: r.ours - ours, later: sky }
  })
  const t = commentsToImport(report, frontierMs)
  return { rows: out, total: { sky: t.sky, ours: t.ours, later: t.laterInSprinklr }, hasLater: late.length > 0, lastFullDayMs: t.lastFullDayMs }
}

/**
 * The headline's comment comparison: whole UTC days up to Platinum's last
 * import, both sides, so a day Platinum has not imported yet is not read as a
 * shortfall. What Sprinklr holds on the later days is counted apart.
 */
export function commentsToImport(
  report: Pick<CommentReport, 'skyTotal' | 'oursTotal' | 'days'>,
  frontierMs: number | undefined,
): { sky: number; ours: number; laterInSprinklr: number; lastFullDayMs: number } {
  const late = (report.days ?? []).filter((d) => dayAfterImport(d.dayMs, frontierMs))
  const lateSky = late.reduce((a, d) => a + d.sky, 0)
  const lateOurs = late.reduce((a, d) => a + d.ours, 0)
  const lastFullDayMs = frontierMs && frontierMs > 0 ? Math.floor(frontierMs / DAY_MS) * DAY_MS - DAY_MS : 0
  return { sky: report.skyTotal - lateSky, ours: report.oursTotal - lateOurs, laterInSprinklr: lateSky, lastFullDayMs }
}

/** What a reason does to the numbers, the post count first: "−14 posts", "No change to the post count · reach +5.92M". */
export function stepEffect(a: ReconMeasures): string {
  const parts = [a.posts === 0 ? 'No change to the post count' : `${signed(a.posts)} post${Math.abs(a.posts) === 1 ? '' : 's'}`]
  const metrics: Array<[string, number]> = [['reach', a.reach], ['views', a.views], ['engagements', a.engagements]]
  for (const [name, v] of metrics) {
    if (Math.round(v) !== 0) parts.push(`${name} ${v > 0 ? '+' : '−'}${fmtReach(Math.abs(Math.round(v)))}`)
  }
  return parts.join(' · ')
}

/** What Platinum adds on top of Sprinklr's counts: comments whose text it coded. */
export function addedOnTop(comments: CommentReport): { held: number; coded: number; x: number; byPlatform: Array<{ platform: string; label: string; coded: number }> } {
  const lines = (comments.lines ?? []).filter((l) => l.imported && !isOwnPostsLine(l))
  const x = lines.filter((l) => canonicalPlatform(l.platform) === 'twitter').reduce((a, l) => a + l.ours, 0)
  const byPlatform = RECON_PLATFORMS.filter((p) => p !== 'twitter')
    .map((p) => ({ platform: p, label: platformLabel(p), coded: lines.filter((l) => canonicalPlatform(l.platform) === p).reduce((a, l) => a + (l.coded ?? 0), 0) }))
    .filter((r) => lines.some((l) => canonicalPlatform(l.platform) === r.platform))
  return { held: comments.oursTotal, coded: comments.oursCoded ?? 0, x, byPlatform }
}
